import { GridApi, RowNode } from "ag-grid-community";
import { useFetchSchemaLookup } from "helpers/merchant/lookup/hooks";
import { ILookupFormattedRow, IValueTypeMap } from "helpers/merchant/lookup/types";
import { ISchemaKey } from "helpers/product-collection";
import React from "react";

const SelectValueContext = React.createContext<{ options: { label: string; value: string }[] }>(null);

interface Props {
    lookupOptions?: { schema?: ISchemaKey; dependenciesValues: IValueTypeMap; merchantId: string };
    useAssortmentValues?: boolean;
    options?: OptionType[];
    children: React.ReactNode;
    gridApi: GridApi;
}

export type OptionType = {
    label: string;
    value: string;
};

export const SelectValueProvider = React.memo((props: Props) => {
    const { lookupOptions, children, options: forcedOptions, useAssortmentValues, gridApi } = props;
    const { schema, dependenciesValues, merchantId } = lookupOptions || {};
    // TODO Fix type assertion because sometime we don't have the schema key
    const rawLookupValues = useFetchSchemaLookup(schema ?? ({} as ISchemaKey), dependenciesValues, merchantId);

    // This is basically getting values in this order
    // 1. options from props
    // 2. lookup
    // 3. schema constraint values
    // 4. assortment values
    // First to hit is returned, also filtering the opions by assortment values
    // if needed
    const mappedOptions = React.useMemo(() => {
        if (forcedOptions) {
            return forcedOptions;
        }

        const getAssortmentOptions = (filter = dependenciesValues) =>
            getOptionsFromAssortment(gridApi, schema?.field_name, filter);

        // Lookup values
        const lookupOptions = rawLookupValues?.rows ?? [];
        const mappedLookupOptions = lookupOptions.map(mapLookupValues);

        if (mappedLookupOptions.length > 0) {
            return useAssortmentValues
                ? filterOptions(mappedLookupOptions, getAssortmentOptions())
                : mappedLookupOptions;
        }

        // Constraint values
        const optionsFromSchema = getAllowedValuesFromSchema(schema);

        if (optionsFromSchema.length > 0) {
            return useAssortmentValues ? filterOptions(optionsFromSchema, getAssortmentOptions()) : optionsFromSchema;
        }

        // Assortment Values
        return getAssortmentOptions();
    }, [rawLookupValues, useAssortmentValues, gridApi, schema, dependenciesValues, forcedOptions]);

    return <SelectValueContext.Provider value={{ options: mappedOptions }}>{children}</SelectValueContext.Provider>;
});

export const useSelectValue = () => React.useContext(SelectValueContext);

const mapLookupValues = (lookupValue: ILookupFormattedRow) => ({
    label: lookupValue.value.dropdownFormatted?.replace(";", "-"),
    value: lookupValue.value.raw.toString(),
});

const getOptionsFromAssortment = (gridApi: GridApi, fieldName: string, filters?: IValueTypeMap) => {
    if (!fieldName) {
        return [];
    }

    const filteredValues = getFilteredValues({ gridApi, fieldName, filters });
    const uniqueValues = [...new Set(filteredValues)];
    const mappedOptions = uniqueValues.map((value) => ({
        label: value,
        value: value,
    }));
    return mappedOptions;
};

const getFilteredValues = ({
    gridApi,
    filters,
    fieldName,
}: {
    gridApi: GridApi;
    filters?: IValueTypeMap;
    fieldName: string;
}) => {
    const filteredValues: string[] = [];

    const filterNodes = (rowNode: RowNode) => {
        if (rowNode.group) {
            const childrenNodes = rowNode.childrenAfterGroup;
            childrenNodes.forEach(filterNodes);
            return;
        }

        const value = rowNode.data.fields[fieldName];
        let isRowAllowed = true;

        if (filters) {
            const filterEntries = filters ? Object.entries(filters) : [];
            const mappedFilters = filterEntries.map((filter) => ({ field: filter[0], value: filter[1].value }));
            isRowAllowed = isRowNodeAllowed(rowNode, mappedFilters);
        }

        if (value?.string_value && isRowAllowed) {
            filteredValues.push(value.string_value);
        }
    };

    gridApi.forEachNode(filterNodes);

    return filteredValues;
};

const isRowNodeAllowed = (row: RowNode, filters: { field: string; value: string }[], isAllowed = true): boolean => {
    const [currentFilter, ...restFilters] = filters;

    if (!isAllowed) {
        return false;
    }

    if (!currentFilter) {
        return true;
    }

    const newIsAllowed = row.data.fields[currentFilter.field]?.string_value === currentFilter.value;

    return isRowNodeAllowed(row, restFilters, newIsAllowed);
};

const getAllowedValuesFromSchema = (schemaField: ISchemaKey): OptionType[] => {
    const allowedValues = schemaField?.constraint?.allowed_values;
    if (!allowedValues) {
        return [];
    }
    const mappedOptions: OptionType[] = allowedValues.reduce((acc: OptionType[], { string_value }) => {
        if (string_value) {
            acc.push({ label: string_value, value: string_value });
        }
        return acc;
    }, []);
    return mappedOptions;
};

const filterOptions = (baseOptions: OptionType[], paramOptions: OptionType[]): OptionType[] => {
    return baseOptions.filter((baseOption) =>
        paramOptions.some((paramOption) => paramOption.value === baseOption.value)
    );
};
